/** The ? help sheet (spec §10.3): screen description, shortcuts, user-guide link, Send feedback. */
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Kbd } from '@/components/ui/misc'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ApiProblem, api, unwrap } from '@/lib/api/client'
import { t } from '@/lib/text'
import { routeForPath } from './routes'

export type HelpTab = 'help' | 'shortcuts' | 'feedback'

const SHORTCUTS: [string, string][] = [
  ['core.shell.search_shortcut', 'core.help.shortcut_search'],
  ['core.help.keys_help', 'core.help.shortcut_help'],
  ['core.help.keys_save', 'core.help.shortcut_save'],
  ['core.help.keys_submit', 'core.help.shortcut_submit'],
  ['core.help.keys_close', 'core.help.shortcut_close'],
]

export function useHelpTexts() {
  return useQuery({
    queryKey: ['help-text'],
    queryFn: async () => unwrap(await api.GET('/api/v1/help-text/', { params: { query: { page_size: 100 } } })).results,
    staleTime: 5 * 60_000,
  })
}

function ScreenHelp({ screenKey, module }: { screenKey?: string; module?: string }) {
  const { data } = useHelpTexts()
  const text = data?.find((entry) => entry.kind === 'screen' && entry.screen_key === screenKey)
  return (
    <div className="flex flex-col gap-3">
      <p className="whitespace-pre-line">{text?.body ?? t('core.help.no_help')}</p>
      {module && (
        <Button asChild variant="outline" className="self-start">
          <Link to={`/help/${module}#${screenKey ?? ''}`}>{t('core.help.guide')}</Link>
        </Button>
      )}
    </div>
  )
}

function Feedback({ page }: { page: string }) {
  const [text, setText] = useState('')
  const send = useMutation({
    mutationFn: async () => unwrap(await api.POST('/api/v1/feedback/', { body: { page, text } })),
    onSuccess: () => setText(''),
  })
  const error = send.error instanceof ApiProblem ? (send.error.fieldErrors.text?.[0] ?? send.error.detail) : undefined
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        send.mutate()
      }}
    >
      <Label htmlFor="feedback-text">{t('core.help.feedback_label')}</Label>
      <p id="feedback-hint" className="text-xs text-muted-foreground">
        {t('core.help.feedback_hint')}
      </p>
      <Textarea
        id="feedback-text"
        value={text}
        onChange={(event) => setText(event.target.value)}
        aria-describedby={error ? 'feedback-hint feedback-error' : 'feedback-hint'}
        aria-invalid={!!error}
        rows={5}
        required
      />
      {error && (
        <p id="feedback-error" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <p aria-live="polite" className="text-sm text-status-success">
        {send.isSuccess ? t('core.help.feedback_sent') : ''}
      </p>
      <Button type="submit" className="self-start" disabled={send.isPending}>
        {send.isPending ? t('core.help.feedback_sending') : t('core.help.feedback_send')}
      </Button>
    </form>
  )
}

export function HelpSheet({ tab, onTabChange }: { tab: HelpTab | null; onTabChange: (tab: HelpTab | null) => void }) {
  const { pathname } = useLocation()
  const route = routeForPath(pathname)
  return (
    <Sheet open={tab !== null} onOpenChange={(open) => !open && onTabChange(null)}>
      <SheetContent closeLabel={t('core.shell.close')}>
        <SheetHeader>
          <SheetTitle>{t('core.help.title')}</SheetTitle>
          <SheetDescription className="sr-only">{t('core.help.title')}</SheetDescription>
        </SheetHeader>
        <Tabs value={tab ?? 'help'} onValueChange={(value) => onTabChange(value as HelpTab)} className="px-4 pb-4">
          <TabsList>
            <TabsTrigger value="help">{t('core.help.tab_help')}</TabsTrigger>
            <TabsTrigger value="shortcuts">{t('core.help.tab_shortcuts')}</TabsTrigger>
            <TabsTrigger value="feedback">{t('core.help.tab_feedback')}</TabsTrigger>
          </TabsList>
          <TabsContent value="help">
            <ScreenHelp screenKey={route?.screenKey} module={route?.module} />
          </TabsContent>
          <TabsContent value="shortcuts">
            <dl className="divide-y">
              {SHORTCUTS.map(([keys, label]) => (
                <div key={label} className="flex items-center justify-between gap-4 py-2">
                  <dt>{t(label)}</dt>
                  <dd>
                    <Kbd>{t(keys)}</Kbd>
                  </dd>
                </div>
              ))}
            </dl>
          </TabsContent>
          <TabsContent value="feedback">
            <Feedback page={pathname} />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  )
}
